import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

/**
 * Compact semantic indicator dot used for connection / live / health
 * state next to a label. Backed by the design-system `--status-*` color
 * tokens so it adapts to light/dark and theme switches automatically.
 *
 * Renders a plain `<span>` (inline-block) by default — drop it inline
 * with text, or wrap in a flex container for label + dot patterns. When
 * `pulse` is set the dot emits a soft `ping` halo to read as "live",
 * mirroring the live-status indicators used elsewhere in the app.
 */

const TONE_BG: Record<NonNullable<StatusDotTone>, string> = {
  success: 'bg-status-success',
  warning: 'bg-status-warning',
  error: 'bg-status-error',
  info: 'bg-status-info',
  neutral: 'bg-status-neutral',
  muted: 'bg-muted-foreground/60',
};

type StatusDotTone = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'muted';

const statusDotVariants = cva('relative inline-flex shrink-0 rounded-full', {
  variants: {
    tone: TONE_BG,
    size: {
      sm: 'size-1.5',
      md: 'size-2',
      lg: 'size-2.5',
    },
  },
  defaultVariants: {
    tone: 'neutral',
    size: 'md',
  },
});

export interface StatusDotProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof statusDotVariants> {
  /**
   * When true, the dot emits a soft expanding halo to read as a "live"
   * heartbeat. Use sparingly — only for actively-transitioning states
   * (connecting, working, syncing). Steady states should not pulse.
   */
  pulse?: boolean;
  /**
   * Accessible label announced by screen readers. Defaults to the dot
   * being marked `aria-hidden` (purely decorative when it sits beside a
   * text label). Provide a label only when the dot is the sole
   * indicator of state.
   */
  label?: string;
}

function StatusDot({ className, tone, size, pulse, label, ...props }: StatusDotProps) {
  const ariaHidden = label ? undefined : true;
  return (
    <span
      data-slot="status-dot"
      data-tone={tone ?? 'neutral'}
      data-size={size ?? 'md'}
      aria-label={label}
      aria-hidden={ariaHidden}
      role={label ? 'status' : undefined}
      className={cn(statusDotVariants({ tone, size }), className)}
      {...props}
    >
      {pulse ? (
        <span
          aria-hidden
          className={cn(
            'absolute inset-0 animate-ping rounded-full opacity-75',
            TONE_BG[tone ?? 'neutral'],
          )}
        />
      ) : null}
    </span>
  );
}

export { StatusDot, statusDotVariants };
